/*
 * Arrows for a horizontal card strip (item 3b).
 *
 * Shared by the category rows and by Cerita Kami, both of which gained a Stacked / Scroll layout
 * setting. It only ever runs inside a `<mh-scroller>`, which those sections render **only** in
 * Scroll mode, so Stacked is untouched by definition.
 *
 * The arrows are rendered `hidden` and revealed from here, and only when the strip can actually
 * scroll. That gives the required behaviour for free in the case where everything already fits at
 * the current width, for example four cards on a desktop row: nothing is scrollable, so neither
 * arrow appears. It also means a failed script leaves a plain swipeable strip rather than two dead
 * buttons.
 *
 * Position is read from scrollLeft on every scroll rather than tracked in a variable, so a swipe,
 * a keyboard scroll and an arrow press all agree without any of them telling the others.
 *
 * **Why the whole file is inside an IIFE.** Two sections on the homepage render a scroller (the
 * category rows and Cerita Kami) and each emits its own `<script src="mh-scroller.js">`. A classic
 * script is evaluated once per tag, not once per URL, so the second evaluation re-ran a top-level
 * `class MhScroller`, and a lexical declaration cannot be made twice in the same scope: the browser
 * threw `Identifier 'MhScroller' has already been declared` on every homepage load. The
 * `customElements.define` guard below never got the chance to help, because the throw happened at
 * parse time, before it. Wrapping the file gives each evaluation its own scope, which is the same
 * shape `mh-stories-modal.js` and `mh-zoom.js` already use. See docs/DECISIONS.md D80.
 */

(function () {
  'use strict';

  // Already defined by the first copy of this script on the page; nothing left to do.
  if (customElements.get('mh-scroller')) return;

  class MhScroller extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-mh-scroller-track]');
      this.prev = this.querySelector('[data-mh-scroller-prev]');
      this.next = this.querySelector('[data-mh-scroller-next]');
      if (!this.track || !this.prev || !this.next) return;

      this.prev.addEventListener('click', () => this.step(-1));
      this.next.addEventListener('click', () => this.step(1));

      // rAF-throttled: scroll fires far more often than the arrows can meaningfully change.
      let ticking = false;
      this.track.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            this.sync();
            ticking = false;
          });
        },
        { passive: true }
      );

      // A width change can turn a scrollable strip into one that fits, and back.
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.sync());
        this.observer.observe(this.track);
      }
      window.addEventListener('resize', this.onResize);

      this.sync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('resize', this.onResize);
    }

    onResize = () => this.sync();

    /** One card plus the gap between cards. Falls back to most of the visible strip. */
    get stepDistance() {
      const card = this.track.firstElementChild;
      if (!card) return Math.round(this.track.clientWidth * 0.8);
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return Math.round(card.getBoundingClientRect().width + gap);
    }

    step(direction) {
      this.track.scrollBy({
        left: this.stepDistance * direction,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }

    sync() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;

      // A pixel of slack: browsers report fractional scroll positions at some zoom levels and
      // device pixel ratios, so an exact comparison leaves an arrow visible at the very end.
      const canScroll = scrollWidth - clientWidth > 2;
      const atStart = scrollLeft <= 2;
      const atEnd = scrollLeft >= scrollWidth - clientWidth - 2;

      this.prev.hidden = !canScroll || atStart;
      this.next.hidden = !canScroll || atEnd;

      // Drives the peek: no partial card when there is nothing to scroll to.
      this.classList.toggle('is-scrollable', canScroll);
    }
  }

  customElements.define('mh-scroller', MhScroller);
})();
